import type { HTMLAttributes, MouseEventHandler, Ref } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '#core/utils/cn.ts'
import { Tooltip } from '#ynt-design-system/components/tooltip/index.ts'
import { Avatar, AVATAR_SIZE_CLASS } from '#ynt-design-system/components/avatar/avatar.tsx'
import type { AvatarSize } from '#ynt-design-system/components/avatar/avatar.tsx'
import type { AvatarColor } from '#ynt-design-system/components/avatar/avatar-identity.ts'

export interface AvatarStackPerson {
  name: string
  src?: string
  color?: AvatarColor
}

export interface AvatarStackProps extends Omit<HTMLAttributes<HTMLElement>, 'onClick'> {
  people: AvatarStackPerson[]
  /** Avatars shown before the rest collapse into a "+N" chip. */
  max?: number
  size?: AvatarSize
  onClick?: MouseEventHandler<HTMLElement>
  ref?: Ref<HTMLElement>
}

const STACK_SIZE_CLASS: Record<AvatarSize, string> = {
  'extra-small': 'ynt-avatar-stack-extra-small',
  small: 'ynt-avatar-stack-small',
  medium: 'ynt-avatar-stack-medium',
  large: 'ynt-avatar-stack-large',
}

/**
 * Overlapping avatars for a group of people, capped at `max` plus a "+N" chip. Each face names
 * itself in a tooltip and the chip lists who it hides; the stack as a whole is one control.
 */
export function AvatarStack({
  people,
  max = 4,
  size = 'small',
  onClick,
  className,
  ref,
  'aria-label': ariaLabel,
  ...rest
}: AvatarStackProps) {
  const { t, i18n } = useTranslation('general')
  const shown = people.length > max ? people.slice(0, max) : people
  const hidden = people.slice(shown.length)

  const label =
    ariaLabel ??
    (people.length === 0
      ? t('avatar.empty')
      : new Intl.ListFormat(i18n.language, { type: 'conjunction' }).format(
          people.map((person) => person.name),
        ))

  const Root = onClick ? 'button' : 'span'

  return (
    <Root
      ref={ref as Ref<HTMLButtonElement & HTMLSpanElement>}
      type={onClick ? 'button' : undefined}
      role={onClick ? undefined : 'img'}
      aria-label={label}
      onClick={onClick}
      className={cn(
        'ynt-avatar-stack',
        STACK_SIZE_CLASS[size],
        onClick && 'ynt-avatar-stack-interactive',
        className,
      )}
      {...rest}
    >
      {people.length === 0 && <Avatar size={size} aria-hidden="true" />}
      {shown.map((person, index) => (
        <Avatar
          key={index}
          name={person.name}
          src={person.src}
          color={person.color}
          size={size}
          aria-hidden="true"
        />
      ))}
      {hidden.length > 0 && (
        <Tooltip
          label={t('avatar.moreCount', { count: hidden.length })}
          values={hidden.map((person) => ({ label: person.name }))}
        >
          <span
            className={cn('ynt-avatar', AVATAR_SIZE_CLASS[size], 'ynt-avatar-overflow')}
            aria-hidden="true"
          >
            <span className="ynt-avatar-label">+{hidden.length}</span>
          </span>
        </Tooltip>
      )}
    </Root>
  )
}

export default AvatarStack
